// PRD 13 §3.1 — list orchestrator: filter bar, table, pagination, and
// loading / empty / no-search-match / error states.
//
// Search is debounced (prototype parity). Sort + pagination run client-side
// over the full list (contract §4 Q1). Row click opens the edit form (Slice 4);
// the Root-only row delete is wired here.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellOff, Search } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { Pagination } from '@/components/data/Pagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useNoticesQuery } from '../hooks/use-notices';
import { useNoticesPageStore } from '../store/notices-page.store';
import type { NoticeSummary } from '../types/notices.types';
import { filterNotices, sortNotices } from '../utils/filter-sort';
import { DeleteNoticeConfirm } from './DeleteNoticeConfirm';
import { NoticeTable } from './NoticeTable';

const SEARCH_DEBOUNCE_MS = 300;

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ height: 40, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
        />
      ))}
    </div>
  );
}

function TableError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('notices');
  return (
    <div
      role="alert"
      style={{
        margin: 16,
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('list.retry')}
      </button>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  return (
    <div className="table-empty">
      <i>
        <BellOff size={48} color="var(--muted-foreground)" />
      </i>
      <p className="table-empty__title">{title}</p>
    </div>
  );
}

export function NoticeManagementView() {
  const { t } = useTranslation('notices');
  // PRD 13 §3.4 line 199 / §8.3 — the Delete column is always present; the
  // button is enabled only for Root (no PUBLICATION:DELETE level). Every
  // non-Root role ("Root 외 전체", READ included) sees it disabled with a
  // tooltip rather than a missing control (SHCT-602).
  const isRoot = useAuthStore((s) => s.isRoot);

  const search = useNoticesPageStore((s) => s.search);
  const setSearch = useNoticesPageStore((s) => s.setSearch);
  const sortKey = useNoticesPageStore((s) => s.sortKey);
  const sortDir = useNoticesPageStore((s) => s.sortDir);
  const toggleSort = useNoticesPageStore((s) => s.toggleSort);
  const page = useNoticesPageStore((s) => s.page);
  const setPage = useNoticesPageStore((s) => s.setPage);
  const openEdit = useNoticesPageStore((s) => s.openEdit);

  const [deleteTarget, setDeleteTarget] = useState<NoticeSummary | null>(null);

  const { data, isLoading, error, refetch } = useNoticesQuery();
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const visibleRows = useMemo(() => {
    if (!data) return [];
    return sortNotices(filterNotices(data, debouncedSearch), sortKey, sortDir);
  }, [data, debouncedSearch, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / DEFAULT_PAGE_SIZE));

  // Clamp `page` if filtering shrank the list below the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);

  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * DEFAULT_PAGE_SIZE;
    return visibleRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [visibleRows, safePage]);

  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <>
      <div className="filter-bar">
        <div className="filter-bar__search" style={{ flex: 1 }}>
          <input
            type="text"
            className="filter-bar__search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            aria-label={t('list.searchPlaceholder')}
            // [공통] 00 §3 — SEARCH class: 1~50 chars.
            maxLength={50}
          />
          <Search size={16} className="filter-bar__search-icon" />
        </div>
        {/* PRD §2.2 #2 — fixed "20 per page" select. */}
        <select
          className="filter-bar__select"
          value={DEFAULT_PAGE_SIZE}
          aria-label={t('list.itemsPerPage', { count: DEFAULT_PAGE_SIZE })}
          onChange={() => {
            /* fixed for now */
          }}
          style={{ marginLeft: 'auto' }}
        >
          <option value={DEFAULT_PAGE_SIZE}>
            {t('list.itemsPerPage', { count: DEFAULT_PAGE_SIZE })}
          </option>
        </select>
      </div>

      <div className="card">
        {isLoading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError message={t('list.loadError')} onRetry={() => void refetch()} />
        ) : (
          <>
            <NoticeTable
              rows={pageRows}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
              onRowOpen={(row) => openEdit(row.id)}
              deleteEnabled={isRoot}
              onDelete={setDeleteTarget}
            />
            {visibleRows.length === 0 && (
              <EmptyState title={hasSearch ? t('list.emptyNoMatch') : t('list.empty')} />
            )}
          </>
        )}
      </div>
      <Pagination page={safePage} totalPages={totalPages} onPageChange={setPage} />

      <DeleteNoticeConfirm notice={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </>
  );
}
